/* =====================================================================
 * DEPRECATED — DO NOT MODIFY.
 *
 * This component (`BriefComponent`) was the brief editor that lived at
 * `/story/:slug/draft` (the "/draft" wizard step 3). It has been superseded
 * by the `/my-drafts` page (see `src/features/MyBriefs/`) which is now the
 * single source of truth for editing, translating, copying, and publishing
 * briefs.
 *
 * Per product decision (2026-06-12), no new features should be added here
 * and no existing bugs should be fixed here — make the change on the
 * `/my-drafts` editor (`MyBriefsExpandedContent.jsx`) instead.
 *
 * The component is retained for now only because legacy navigation paths
 * may still reach it. Once we confirm no production traffic hits the
 * `/draft` route, delete this file outright.
 * ===================================================================== */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { api } from "../../app/http-common";
import LottieLoader from '../../components/LottieLoader';
import "./BriefComponent.css";
import {getSourceTitle2, languages as LANGUAGES_LIST} from "./transformers";
import { getSourcesLabel, isRtlLanguageName } from "./briefI18n";
import { toIso2 } from "../../utils/languages";
import { confineQuillTooltip } from '../../utils/quillLinkTooltip';
import { Editor } from 'primereact/editor';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
// PrimeReact's Editor wraps Quill, whose "snow" theme CSS makes the .ql-editor
// content (title, subheader, body) visible. Under the Vite production build this
// CSS is no longer pulled in transitively the way it was under CRA, so import it
// explicitly — without it the Editor mounts but the text renders blank.
import 'quill/dist/quill.snow.css';
//import 'primeicons/primeicons.css';
import { useIsAdmin, useCanEdit } from '../../hooks/useFrontegg';

/**
 * PRD section 2.1. Bernd, on the German articles reaching Ippen: "the subheads
 * are still just bold and not real H2."
 *
 * The body editor had `showHeader={false}` and no formats override, so there was
 * no heading control at all and bold was the only tool an editor had. Orr
 * confirmed editors mark subheadings themselves, so withholding the control was
 * half the defect: the other half is that nothing downstream understood a real
 * heading, which the enrichment and the Ippen adapter now do.
 *
 * `formats` and `modules` must list the same things as the toolbar. Quill
 * silently discards a format absent from the list, which renders as a button
 * that appears to do nothing. Same pairing as the campaign report editor.
 */
const BODY_EDITOR_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'blockquote', 'list', 'link'];

const BODY_EDITOR_MODULES = {
    toolbar: [
        // Only H2. A brief's title and standfirst are separate fields, so every
        // heading inside the body is a section subheading, which is the single
        // level NITF's hl2 represents.
        [{ header: 2 }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'link'],
        ['clean'],
    ],
};

const BODY_EDITOR_HEADER = (
    <>
        <span className="ql-formats">
            <button type="button" className="ql-header" value="2" aria-label="Subheading" />
        </span>
        <span className="ql-formats">
            <button type="button" className="ql-bold" aria-label="Bold" />
            <button type="button" className="ql-italic" aria-label="Italic" />
            <button type="button" className="ql-underline" aria-label="Underline" />
            <button type="button" className="ql-strike" aria-label="Strike" />
        </span>
        <span className="ql-formats">
            <button type="button" className="ql-list" value="ordered" aria-label="Ordered list" />
            <button type="button" className="ql-list" value="bullet" aria-label="Bullet list" />
            <button type="button" className="ql-blockquote" aria-label="Blockquote" />
        </span>
        <span className="ql-formats">
            <button type="button" className="ql-link" aria-label="Link" />
            <button type="button" className="ql-clean" aria-label="Clear formatting" />
        </span>
    </>
);

const BriefComponent = ({
                            briefResponse,
                            setSelectedTitleIndex,
                            selectedTitleIndex,
                            setBriefResponse,
                            briefSourceArticles,
                            selectedClaims,
                            isRtl,
                            selectedCategory,
                            comparisonBriefs,
                            generationErrors,
                            storyId,
                            storyTitle,
                            language,
                            onToast,
                            onCopySuccess

                        }) => {
    const isAdmin = useIsAdmin();
    const canEdit = useCanEdit();
    const [viewMode, setViewMode] = useState('genesis');
    const [isCopied, setIsCopied] = useState(false);
    const [loadingBrief, setLoadingBrief] = useState(false);
    const [selectedReformatOption, setSelectedReformatOption] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [reformatBrief, setReformatBrief] = useState('');
    
    // Publish to CMS state
    const [showPublishDialog, setShowPublishDialog] = useState(false);
    const [publishClients, setPublishClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState('');
    const [publishStatus, setPublishStatus] = useState('draft');
    const [isPublishing, setIsPublishing] = useState(false);
    const [publishResult, setPublishResult] = useState(null);
    const [comparisonView, setComparisonView] = useState('claimsBased'); // 'claimsBased' or 'claimFree'
    const [processedComparisonBriefs, setProcessedComparisonBriefs] = useState(null); // Processed comparison briefs

    // Post-edit translation state — lets the editor translate the currently
    // edited brief (titles + subheader + body) to a new target language without
    // leaving the app.
    const [translateTargetLanguage, setTranslateTargetLanguage] = useState(language || 'English');
    const [isTranslating, setIsTranslating] = useState(false);

    useEffect(() => {
        // When the brief's source language changes upstream, keep the dropdown
        // in sync but default away from the source so the user picks a target.
        if (language) setTranslateTargetLanguage(language);
    }, [language]);

    // Strip a trailing "Sources:" footer (any supported language) from an
    // HTML brief body so we don't translate the footer text. The footer is
    // re-rendered for the new target language by the existing source-injection
    // effect after the translated brief is set.
    const stripSourcesFooterHtml = (html) => {
        if (!html) return html;
        const altLabels = ['Sources', 'מקורות', 'Quellen', 'Fuentes', 'Fontes', 'Fonti', 'Источники', 'Πηγές', 'Bronnen', 'Kilder', 'Zdroje', 'Kaynaklar', 'Джерела', 'منابع', 'المصادر', '来源', 'स्रोत'].join('|');
        return html.replace(new RegExp(`(?:<p[^>]*>\\s*)?(?:${altLabels})\\s*:[\\s\\S]*$`, 'i'), '');
    };

    const handleTranslateBrief = async () => {
        if (!briefResponse) return;
        const targetCode = toIso2(translateTargetLanguage) || 'en';
        const sourceCode = toIso2(language) || 'en';
        if (targetCode === sourceCode) {
            if (onToast) onToast('Brief is already in this language', 'info');
            return;
        }
        setIsTranslating(true);
        try {
            const bodyForTranslate = stripSourcesFooterHtml(briefResponse.brief || '');
            const titles = Array.isArray(briefResponse.titles) ? briefResponse.titles : [];
            const subheader = briefResponse.subheader || '';

            const payload = [
                ...titles,
                subheader,
                bodyForTranslate,
            ];

            const translated = await api.translateFast({ texts: payload, target: targetCode, draft: true });
            if (!Array.isArray(translated) || translated.length !== payload.length) {
                throw new Error('Unexpected translation response');
            }

            const translatedTitles = translated.slice(0, titles.length);
            const translatedSubheader = translated[titles.length];
            const translatedBody = translated[titles.length + 1];

            // Reset the source-injection effect so it appends the localized
            // "Sources:" footer in the new language.
            briefProcessedFlag.current = false;
            processedBriefRef.current = null;

            setBriefResponse((prev) => ({
                ...prev,
                titles: translatedTitles,
                header: translatedTitles[selectedTitleIndex] || prev?.header,
                subheader: translatedSubheader,
                brief: translatedBody,
                translatedTo: targetCode,
            }));
            if (onToast) onToast(`Brief translated to ${translateTargetLanguage}`, 'success');
        } catch (err) {
            console.error('Translation failed:', err);
            if (onToast) onToast('Translation failed', 'error');
        } finally {
            setIsTranslating(false);
        }
    };
     // Default to first title
    const contentEditableRef = useRef(null);
    const processedBriefRef = useRef(null); // Track the raw AI brief text we already processed
    const briefProcessedFlag = useRef(false); // Flag to prevent reprocessing after Quill edits
    
    // Convert plain text brief (with \n) to HTML for the Editor component
    const convertBriefToHtml = (text) => {
        if (!text) return text;
        // If it already has proper paragraph structure, return as-is.
        // `<h2>` matters as much as `<h1>` since PRD section 2.1: an enriched
        // brief whose only block elements are headings would otherwise be
        // re-split and its headings flattened into paragraphs.
        if (text.includes('<p>') || /<h[1-6][\s>]/i.test(text)) return text;
        // If text has no newlines at all, try to split into paragraphs by sentences
        if (!text.includes('\n')) {
            // Insert paragraph breaks every ~3 sentences
            let sentenceCount = 0;
            const withBreaks = text.replace(/([.!?])\s+(?=[A-Z\u00C0-\u024F\u0400-\u04FF])/g, (match, punct) => {
                sentenceCount++;
                if (sentenceCount % 3 === 0) {
                    return `${punct}\n\n`;
                }
                return match;
            });
            if (!withBreaks.includes('\n')) return `<p>${text}</p>`;
            text = withBreaks;
        }
        // Convert \n\n (paragraph breaks) to <p> tags and single \n to <br>
        let html = text
            .split(/\n\n+/)
            .map(para => para.trim())
            .filter(para => para.length > 0)
            // PRD section 2.1: a subheading written as "## Heading" on its own
            // line becomes a real heading rather than a paragraph of hashes.
            .map(para => {
                const heading = para.match(/^#{2,6}\s+(.+)$/);
                return heading
                    ? `<h2>${heading[1].trim()}</h2>`
                    : `<p>${para.replace(/\n/g, '<br>')}</p>`;
            })
            .join('');
        return html;
    };

    // Effect to handle comparison view switching
    useEffect(() => {
        if (comparisonBriefs && comparisonView) {
            const selectedBrief = comparisonView === 'claimsBased' 
                ? comparisonBriefs.claimsBased 
                : comparisonBriefs.claimFree;
            
            if (selectedBrief && selectedBrief.brief) {
                // Update the briefResponse with the selected comparison brief
                // Convert \n to HTML so the Editor renders paragraph breaks
                setBriefResponse((prev) => ({
                    ...prev,
                    ...selectedBrief,
                    brief: convertBriefToHtml(selectedBrief.brief),
                    generationMethod: comparisonView
                }));
            }
        }
    }, [comparisonView, comparisonBriefs]);

    const toggleDropdown = () => {
        setIsDropdownOpen(!isDropdownOpen);
    };

    const handleReformatContentChange = (event) => {
        setReformatBrief(event.target.value);
    };

    const getCaretPosition = (element) => {
        let caretOffset = 0;
        if (window.getSelection) {
            const range = window.getSelection().getRangeAt(0);
            const preCaretRange = range.cloneRange();
            preCaretRange.selectNodeContents(element);
            preCaretRange.setEnd(range.endContainer, range.endOffset);
            caretOffset = preCaretRange.toString().length;
        }
        return caretOffset;
    };

    const setCaretPosition = (element, offset) => {
        const range = document.createRange();
        const sel = window.getSelection();
        range.setStart(element.childNodes[0], offset);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    };

    const handleHtmlInput = () => {
        const caretPosition = getCaretPosition(contentEditableRef.current);
        setReformatBrief(contentEditableRef.current.innerHTML);
        setTimeout(() => {
            setCaretPosition(contentEditableRef.current, caretPosition);
        }, 0);
    };

    function fixHebrewPunctuation(text) {
        // Move comma outside the quotes
        let fixedText = text?.replace(/"([^"]+),"/g, '"$1",');

        // Move period outside the quotes
        fixedText = fixedText?.replace(/"([^"]+)\."(\s|$)/g, '"$1".');

        return fixedText;
    }

    function replaceArticleNamesWithLinks(text) {
        // If text already contains anchor tags, skip to avoid nesting <a> inside <a>
        if (!text || text.includes('<a ')) {
            return text;
        }

        const briefSourceArticlesClean = [...new Map(briefSourceArticles.map(article => [article.name, article])).values()];

        if (!Array.isArray(briefSourceArticlesClean) || briefSourceArticlesClean.length === 0) {
            return text;
        }

        let successCount = 0; // Counter to track successful replacements

        briefSourceArticlesClean.forEach(({ name, url }, index) => {
            if (successCount >= 2) {
                return; // Stop processing after two successful replacements
            }

            if (!name || !url) {
                return;
            }

            // Skip if the name looks like a URL (contains slashes beyond protocol)
            // This prevents creating <a> tags where the display text is a raw URL
            if (name.includes('/') && name.length > 40) {
                return;
            }

            // Create proper HTML anchor tag instead of the bracket format
            const replacement = `<a href="${url}" target="_blank" rel="noopener noreferrer">${name}</a>`;

            // Replace only the first occurrence of the article name in the text
            const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // Escape special characters in the name
            const regex = new RegExp(`\\b${escapedName}\\b`); // No 'g' flag ensures only the first match is replaced

            const newText = text.replace(regex, replacement);

            // Check if a replacement occurred
            if (newText !== text) {
                text = newText; // Update the text with the replacement
                successCount++; // Increment the counter
            }
        });

        return text;
    }

    // New function to convert existing "Source [URL]" format to hyperlinks
    function convertExistingBracketLinksToHyperlinks(html) {
        // Find patterns like "Source [URL]" and convert to <a> tags
        // Update regex to better target source names before URLs
        // Look for patterns like "Financial Times [URL]" or "according to the Financial Times [URL]"
        return html.replace(/(\w+(?:\s+\w+)*)\s*\[(https?:\/\/[^\]]+)\]/g, (match, sourceName, url) => {
            // Check if the source name contains "according to" or similar phrases
            if (/according to (the )?(.*)/i.test(sourceName)) {
                // Extract just the source name after "according to"
                const matches = sourceName.match(/according to (?:the )?([^,\.]+)/i);
                if (matches && matches[1]) {
                    // Replace only the actual source name part
                    const actualSource = matches[1].trim();
                    return sourceName.replace(
                        actualSource,
                        `<a href="${url}" target="_blank" rel="noopener noreferrer">${actualSource}</a>`
                    );
                }
            }
            
            // For cases where it's just "Source [URL]" without "according to"
            // Find the last word group that's likely the source name
            const words = sourceName.split(/\s+/);
            if (words.length > 2) {
                // Look for likely publication names (often 1-3 words)
                // Use likely candidates like "Times", "Post", "Journal", etc.
                const publicationKeywords = ["Times", "Journal", "Post", "News", "Daily", "Herald", "Tribune", "TradingView"];
                
                // Find where the publication name starts
                let startIndex = -1;
                for (let i = words.length - 1; i >= 0; i--) {
                    if (publicationKeywords.some(keyword => words[i].includes(keyword))) {
                        // Found a keyword, now find a good start point
                        startIndex = Math.max(0, i - 2); // Allow up to 2 words before the keyword
                        break;
                    }
                }
                
                if (startIndex !== -1) {
                    // Found a likely publication name
                    const beforeText = words.slice(0, startIndex).join(' ');
                    const pubName = words.slice(startIndex).join(' ');
                    return beforeText + ' ' + 
                        `<a href="${url}" target="_blank" rel="noopener noreferrer">${pubName}</a>`;
                }
            }
            
            // If we're here, we have a simple case or couldn't identify a partial match
            // Use a heuristic - try to extract just the name portion
            const simpleName = sourceName.trim().split(/[,\.]/)[0].trim();
            return sourceName.replace(
                simpleName,
                `<a href="${url}" target="_blank" rel="noopener noreferrer">${simpleName}</a>`
            );
        });
    }

    useEffect(() => {
        // Safely gather unique sources with null checks
        const uniqueSources = selectedClaims && selectedClaims.length > 0 ? 
            [...new Set(selectedClaims
                .filter(c => c && Array.isArray(c.sources)) // Ensure claim and sources array exist
                .flatMap(c => c.sources.map(s => getSourceTitle2(s))))] : 
            [];
            
        const uniqueSourcesString = isRtl ? uniqueSources.join(' ,') : uniqueSources.join(', ');
        const localizedSourcesLabel = getSourcesLabel(language);
        const prefixSourcesString = `\n\n${localizedSourcesLabel}: `;
        let sourcesString = uniqueSources.length > 0 ? 
            prefixSourcesString + uniqueSourcesString : 
            '';
        
        // Make sure briefResponse exists before accessing its properties
        if (!briefResponse || !briefResponse.brief) {
            briefProcessedFlag.current = false;
            return; // Exit early if briefResponse or brief isn't available yet
        }
        
        // Skip if we already processed this brief (prevents infinite loop
        // when Quill editor reformats the HTML and triggers onTextChange)
        if (briefProcessedFlag.current && processedBriefRef.current === briefResponse.brief) {
            if (viewMode !== 'brief') setViewMode('brief');
            return;
        }
        
        // Also skip if the text already contains <a> tags (meaning it was already processed,
        // but Quill may have reformatted it slightly so string comparison fails)
        if (briefProcessedFlag.current && briefResponse.brief.includes('<a ')) {
            if (viewMode !== 'brief') setViewMode('brief');
            return;
        }
        
        // Remove any bold text (**text**) from the brief
        let finalString = briefResponse.brief.replace(/\*\*(.*?)\*\*/g, '$1');

        finalString = replaceArticleNamesWithLinks(finalString);

        finalString = finalString.includes(uniqueSourcesString) ?
            finalString:
            finalString+sourcesString;
        finalString = isRtl? fixHebrewPunctuation(finalString):finalString;
        // Convert paragraph breaks (\n\n) to <p> tags and single \n to <br>
        finalString = convertBriefToHtml(finalString);
        finalString = finalString?.replace(/(Title: |### |\*\*)(.*?)(\*\*|<br>|<\/p>|$)/g, (match, p1, p2, p3) => {
            // Remove trailing ** from the title if it's present
            if (p3 === '**') {
                return `<h1>${p2}</h1>`;
            }
            return `<h1>${p2}</h1>${p3}`;
        });

        // Convert any existing "Source [URL]" patterns to proper hyperlinks
        finalString = convertExistingBracketLinksToHyperlinks(finalString);

        // Remember the processed output so we don't reprocess it
        processedBriefRef.current = finalString;
        briefProcessedFlag.current = true;
        setBriefResponse((prev) => ({
            ...prev,
            brief: finalString,
        }));
        setViewMode('brief')
    }, [briefResponse?.brief]);

    const applyDirectionClass = () => {
        const editorContentElements = document.querySelectorAll('.ql-editor');
        editorContentElements.forEach((element) => {
            if (isRtl) {
                element.classList.add('rtl');
                element.classList.remove('ltr');
            } else {
                element.classList.add('ltr');
                element.classList.remove('rtl');
            }
        });
    };

    useEffect(() => {
        let retryCount = 0;
        const tryApplyDirection = () => {
            const editorContentElements = document.querySelectorAll('.ql-editor');
            if (editorContentElements.length > 0) {
                applyDirectionClass(); // Apply direction if elements are found
            } else if (retryCount < 5) { // Retry up to 5 times
                retryCount++;
                setTimeout(tryApplyDirection, 100); // Retry after 100ms
            } else {
                console.warn('Could not find .ql-editor elements after retries');
            }
        };

        tryApplyDirection(); // Initial attempt
    }, [isRtl]);
    
    
    const displayTitles = useMemo(() => {
        if (!briefResponse?.titles) return [];
        return briefResponse.titles.includes(briefResponse.header)
            ? briefResponse.titles
            : [...briefResponse.titles, briefResponse.header];
    }, [briefResponse?.header, briefResponse?.titles]);
    
    return (
        <div className="brief-container">
            {briefResponse?.brief && (
                <div className="brief-content">

                    {/* §4 — how many distinct publishers this draft actually
                        rests on. Rendered OUTSIDE the editable article text, so
                        it cannot be copied into the published copy and cannot be
                        edited away. The backend already decides the wording and
                        stores it on the result; this is the only place it is
                        shown. Without this the notice existed in the record and
                        nowhere a person would see it. */}
                    {briefResponse?.sourceNotice && (
                        <div
                            role="note"
                            className="brief-source-notice"
                            style={{
                                margin: '0 0 12px',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                background: 'rgba(255,152,0,0.10)',
                                border: '1px solid rgba(255,152,0,0.35)',
                                color: '#ad6300',
                                fontSize: '13px',
                                fontFamily: "'Inter', sans-serif",
                            }}
                        >
                            {briefResponse.sourceNotice}
                            {briefResponse.restrictedScope
                                ? ` Draft sources: ${briefResponse.restrictedScope}.`
                                : ''}
                        </div>
                    )}

                    {/* Comparison Mode Toggle (editor+) */}
                    {comparisonBriefs && canEdit && (
                        <div className="brief-comparison-toggle">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h4 style={{ margin: 0, color: '#333', fontSize: '14px' }}>
                                    Comparison Mode <span style={{ color: '#f4b400', fontSize: '12px' }}>(Beta)</span>
                                </h4>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        onClick={() => setComparisonView('claimsBased')}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: comparisonView === 'claimsBased' ? '#FF6B35' : '#e9ecef',
                                            color: comparisonView === 'claimsBased' ? 'white' : '#333',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: comparisonView === 'claimsBased' ? '600' : '400'
                                        }}
                                        disabled={!comparisonBriefs.claimsBased}
                                    >
                                        Claims-based {!comparisonBriefs.claimsBased && '(Failed)'}
                                    </button>
                                    <button
                                        onClick={() => setComparisonView('claimFree')}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: comparisonView === 'claimFree' ? '#FF6B35' : '#e9ecef',
                                            color: comparisonView === 'claimFree' ? 'white' : '#333',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            fontSize: '13px',
                                            fontWeight: comparisonView === 'claimFree' ? '600' : '400'
                                        }}
                                        disabled={!comparisonBriefs.claimFree}
                                    >
                                        Claim-free {!comparisonBriefs.claimFree && '(Failed)'}
                                    </button>
                                </div>
                            </div>

                            {generationErrors && generationErrors.length > 0 && (
                                <div style={{
                                    backgroundColor: '#fff3cd',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    fontSize: '13px',
                                    color: '#856404'
                                }}>
                                    <strong>Some generation methods failed:</strong>
                                    <ul style={{ margin: '5px 0 0 20px', padding: 0 }}>
                                        {generationErrors.map((err, idx) => (
                                            <li key={idx}>{err.method}: {err.error}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {comparisonView === 'claimFree' && comparisonBriefs.claimFree?.sourcesUsed && (
                                <div style={{
                                    marginTop: '10px',
                                    fontSize: '12px',
                                    color: '#666',
                                    padding: '8px',
                                    backgroundColor: '#e8f4f8',
                                    borderRadius: '4px'
                                }}>
                                    <strong>Sources used:</strong> {comparisonBriefs.claimFree.sourcesUsed.map((s, i) => {
                                        const sourceName = typeof s.source === 'string' ? s.source : (s.source?.title || s.source?.uri || 'Unknown');
                                        return (
                                        <span key={i}>
                                            {s.title} ({sourceName}){s.isPreferredPublisher && ' ⭐'}
                                            {i < comparisonBriefs.claimFree.sourcesUsed.length - 1 && ', '}
                                        </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* HEADLINE section */}
                    <div>
                        <div className="brief-section-label">
                            <span className="brief-section-accent"></span>
                            <span className="brief-section-text headline">HEADLINE</span>
                        </div>
                        {displayTitles.map((title, index) => {
                            const titleString = typeof title === 'string' ? title : (title?.title || title?.text || String(title || ''));
                            return (
                                <div
                                    key={index}
                                    className={`brief-title-option ${selectedTitleIndex === index ? 'selected' : ''}`}
                                    onClick={() => setSelectedTitleIndex(index)}
                                >
                                    <input
                                        type="radio"
                                        className="brief-title-radio"
                                        name="title-selection"
                                        checked={selectedTitleIndex === index}
                                        onChange={() => setSelectedTitleIndex(index)}
                                    />
                                    <div className="brief-title-editor-wrap">
                                        <Editor
                                            onLoad={confineQuillTooltip}
                                            showHeader={false}
                                            spellCheck={true}
                                            value={titleString}
                                            onTextChange={(e) => setBriefResponse((prev) => {
                                                const updatedTitles = [...prev.titles];
                                                updatedTitles[index] = e.htmlValue;
                                                return {...prev, titles: updatedTitles};
                                            })}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* SUBHEADER section */}
                    <div>
                        <div className="brief-section-label">
                            <span className="brief-section-accent subheader"></span>
                            <span className="brief-section-text subheader">SUBHEADER</span>
                        </div>
                        <div className="brief-editor-wrap subheader">
                            <Editor
                                onLoad={confineQuillTooltip}
                                showHeader={false}
                                spellCheck={true}
                                value={briefResponse.subheader}
                                onTextChange={(e) => setBriefResponse((prev) => ({...prev, subheader: e.htmlValue}))}
                            />
                        </div>
                    </div>

                    {/* BODY section */}
                    <div>
                        <div className="brief-section-label">
                            <span className="brief-section-accent body"></span>
                            <span className="brief-section-text body">BODY</span>
                        </div>
                        <div className="brief-editor-wrap body">
                            <Editor
                                onLoad={confineQuillTooltip}
                                headerTemplate={BODY_EDITOR_HEADER}
                                modules={BODY_EDITOR_MODULES}
                                formats={BODY_EDITOR_FORMATS}
                                spellCheck={true}
                                value={briefResponse.brief}
                                onTextChange={(e) => setBriefResponse((prev) => ({...prev, brief: e.htmlValue}))}
                                pt={{
                                    content: { className: 'brief-editor-content' }
                                }}
                            />
                        </div>
                    </div>

                    {/* Actions row */}
                    <div className="brief-actions-row">
                        {canEdit && (
                            <div
                                className="brief-translate-control"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginRight: 'auto',
                                }}
                            >
                                <label
                                    htmlFor="brief-translate-target"
                                    style={{ fontSize: '13px', color: '#3C4A5E', fontWeight: 500 }}
                                >
                                    Translate to:
                                </label>
                                <select
                                    id="brief-translate-target"
                                    aria-label="Brief translation target language"
                                    value={translateTargetLanguage}
                                    onChange={(e) => setTranslateTargetLanguage(e.target.value)}
                                    disabled={isTranslating}
                                    style={{
                                        padding: '8px 10px',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(60,74,94,0.20)',
                                        backgroundColor: '#F7F9FC',
                                        fontSize: '13px',
                                        color: '#0B1E3F',
                                        minWidth: '140px',
                                    }}
                                >
                                    {LANGUAGES_LIST.map((lang) => (
                                        <option key={lang} value={lang}>{lang}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="brief-translate-button"
                                    onClick={handleTranslateBrief}
                                    disabled={isTranslating}
                                    style={{
                                        padding: '10px 18px',
                                        backgroundColor: isTranslating ? '#a5b9d6' : '#0B1E3F',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        cursor: isTranslating ? 'not-allowed' : 'pointer',
                                        fontWeight: '600',
                                        fontFamily: "'Inter', sans-serif",
                                    }}
                                >
                                    {isTranslating ? 'Translating…' : 'Translate'}
                                </button>
                            </div>
                        )}
                        <button
                            className="smart-copy-button"
                            onClick={() => {
                                const contentParts = [];
                                const textContentParts = [];

                                if (selectedCategory && selectedCategory !== 'All categories' && selectedCategory !== 'none') {
                                    contentParts.push(`<div>${selectedCategory}</div>`);
                                    textContentParts.push(selectedCategory);
                                }

                                const titleDiv = document.createElement('div');
                                const titleContent = briefResponse.titles[selectedTitleIndex] || '';
                                titleDiv.innerHTML = titleContent;
                                const titleText = titleDiv.textContent || titleDiv.innerText || '';
                                if (titleText.trim()) {
                                    contentParts.push(`<div><strong>${titleText.trim()}</strong></div>`);
                                    textContentParts.push(titleText.trim());
                                }

                                const subheaderDiv = document.createElement('div');
                                subheaderDiv.innerHTML = briefResponse.subheader || '';
                                const subheaderText = subheaderDiv.textContent || subheaderDiv.innerText || '';
                                if (subheaderText.trim()) {
                                    contentParts.push(`<div>${subheaderText.trim()}</div>`);
                                    textContentParts.push(subheaderText.trim());
                                }

                                const bodyContent = briefResponse.brief || '';
                                let bodyHTML = convertExistingBracketLinksToHyperlinks(bodyContent);
                                // Strip a trailing "Sources:" footer in whatever language the
                                // brief was generated in. We match the localized label for the
                                // current language first, and fall back to any known label so
                                // briefs produced before this change still get stripped.
                                const localizedLabel = getSourcesLabel(language);
                                const escapedLabel = localizedLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                const labelAlternation = [escapedLabel, 'Sources', 'מקורות', 'Quellen', 'Fuentes', 'Fontes', 'Fonti', 'Источники', 'Πηγές', 'Bronnen', 'Kilder', 'Zdroje', 'Kaynaklar', 'Джерела', 'منابع', 'المصادر', '来源', 'स्रोत'].join('|');
                                const sourcesRegex = new RegExp(`(?:<p[^>]*>\\s*)?(?:${labelAlternation})\\s*:[\\s\\S]*$`, 'i');
                                bodyHTML = bodyHTML.replace(sourcesRegex, '');
                                if (bodyHTML.trim()) {
                                    contentParts.push(bodyHTML.trim());
                                    let plainHTML = bodyHTML.replace(/<\/p>\s*<p[^>]*>/gi, '\n\n');
                                    plainHTML = plainHTML.replace(/<br\s*\/?>/gi, '\n');
                                    const bodyDiv = document.createElement('div');
                                    bodyDiv.innerHTML = plainHTML;
                                    const bodyPlainText = bodyDiv.textContent || bodyDiv.innerText || '';
                                    textContentParts.push(bodyPlainText.trim());
                                }
                                const fullHTMLContent = `<div style="font-family: Arial, sans-serif">${contentParts.join('<br><br>')}</div>`;
                                const textToCopy = textContentParts.join('\n');
                                try {
                                    const clipboardItem = new ClipboardItem({
                                        'text/html': new Blob([fullHTMLContent], { type: 'text/html' }),
                                        'text/plain': new Blob([textToCopy], { type: 'text/plain' })
                                    });
                                    navigator.clipboard.write([clipboardItem])
                                        .then(() => {
                                            if (onCopySuccess) onCopySuccess();
                                            if (onToast) onToast('Brief copied', 'success');
                                        })
                                        .catch(err => {
                                            console.error('Failed to copy HTML content: ', err);
                                            navigator.clipboard.writeText(textToCopy).then(() => {
                                                if (onCopySuccess) onCopySuccess();
                                                if (onToast) onToast('Brief copied', 'success');
                                            }).catch(e => {
                                                console.error('Failed to copy as plain text: ', e);
                                            });
                                        });
                                } catch (err) {
                                    console.error('ClipboardItem API not supported: ', err);
                                    navigator.clipboard.writeText(textToCopy).then(() => {
                                        if (onCopySuccess) onCopySuccess();
                                        if (onToast) onToast('Brief copied', 'success');
                                    }).catch(e => {
                                        console.error('Failed to copy as plain text: ', e);
                                    });
                                }
                            }}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#FF6B35',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontFamily: "'Inter', sans-serif",
                            }}
                            aria-label="Copy brief body (excluding sources) to clipboard"
                            title="Copy the brief body (excluding sources)"
                        >
                            Copy Brief
                        </button>

                        {isAdmin && (
                            <button
                                className="publish-cms-button"
                                onClick={async () => {
                                    try {
                                        const clients = await api.getPublishClients();
                                        setPublishClients(clients || []);
                                        if (clients?.length > 0) {
                                            setSelectedClient(clients[0]._id);
                                        }
                                        setPublishResult(null);
                                        setShowPublishDialog(true);
                                    } catch (err) {
                                        console.error('Failed to load publish clients:', err);
                                    }
                                }}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#00AEEF',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontFamily: "'Inter', sans-serif",
                                }}
                            >
                                Publish to CMS
                            </button>
                        )}
                    </div>

                    {/* Publish Dialog */}
                    {isAdmin && showPublishDialog && (
                        <div style={{
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <div style={{
                                backgroundColor: 'white', borderRadius: '10px', padding: '30px',
                                maxWidth: '450px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                            }}>
                                <h3 style={{ marginTop: 0 }}>Publish Draft to CMS</h3>

                                {publishResult ? (
                                    <div style={{ textAlign: 'center' }}>
                                        <p style={{
                                            color: publishResult.status === 'failed' ? '#d32f2f' : '#2e7d32',
                                            fontWeight: 'bold', fontSize: '18px'
                                        }}>
                                            {publishResult.status === 'failed' ? 'Publish Failed' : 'Published Successfully'}
                                        </p>
                                        {publishResult.externalId && (
                                            <p style={{ color: '#666' }}>External ID: {publishResult.externalId}</p>
                                        )}
                                        {publishResult.error && (
                                            <p style={{ color: '#d32f2f', fontSize: '14px' }}>{publishResult.error}</p>
                                        )}
                                        <button
                                            onClick={() => { setShowPublishDialog(false); setPublishResult(null); }}
                                            style={{
                                                marginTop: '15px', padding: '10px 25px',
                                                backgroundColor: '#666', color: 'white', border: 'none',
                                                borderRadius: '5px', cursor: 'pointer', fontSize: '14px'
                                            }}
                                        >Close</button>
                                    </div>
                                ) : (
                                    <>
                                        <div style={{ marginBottom: '15px' }}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                                Target:
                                            </label>
                                            <select
                                                value={selectedClient}
                                                onChange={e => setSelectedClient(e.target.value)}
                                                style={{ width: '100%', padding: '8px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            >
                                                {publishClients.map(c => (
                                                    <option key={c._id} value={c._id}>{c.displayName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                                Status:
                                            </label>
                                            <select
                                                value={publishStatus}
                                                onChange={e => setPublishStatus(e.target.value)}
                                                style={{ width: '100%', padding: '8px', fontSize: '14px', borderRadius: '4px', border: '1px solid #ccc' }}
                                            >
                                                <option value="draft">Draft</option>
                                                <option value="published">Published (Live)</option>
                                            </select>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                            <button
                                                onClick={() => setShowPublishDialog(false)}
                                                style={{
                                                    padding: '10px 20px', backgroundColor: '#e0e0e0', color: '#333',
                                                    border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '14px'
                                                }}
                                            >Cancel</button>
                                            <button
                                                disabled={isPublishing || !selectedClient}
                                                onClick={async () => {
                                                    setIsPublishing(true);
                                                    try {
                                                        const title = briefResponse.titles?.[selectedTitleIndex] || '';
                                                        const result = await api.publishBrief({
                                                            clientId: selectedClient,
                                                            title: title,
                                                            subheader: briefResponse.subheader || '',
                                                            body: briefResponse.brief || '',
                                                            language: language || 'en',
                                                            category: Array.isArray(selectedCategory) ? selectedCategory[0] : selectedCategory,
                                                            storyId: storyId,
                                                            storyTitle: storyTitle,
                                                            status: publishStatus,
                                                            sourceArticles: briefSourceArticles || []
                                                        });
                                                        setPublishResult(result);
                                                    } catch (err) {
                                                        setPublishResult({ status: 'failed', error: err.message || 'Unknown error' });
                                                    } finally {
                                                        setIsPublishing(false);
                                                    }
                                                }}
                                                style={{
                                                    padding: '10px 25px',
                                                    backgroundColor: isPublishing ? '#a5d6a7' : '#4CAF50',
                                                    color: 'white', border: 'none', borderRadius: '5px',
                                                    cursor: isPublishing ? 'not-allowed' : 'pointer',
                                                    fontSize: '14px', fontWeight: 'bold'
                                                }}
                                            >
                                                {isPublishing ? 'Publishing...' : 'Publish'}
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {canEdit && briefResponse?.phaseConfig && (
                        <div className="brief-debug-info">
                            <span>API: {String(briefResponse.phaseConfig.api || '')}</span>
                            <span>Model: {String(briefResponse.phaseConfig.model || '')}</span>
                        </div>
                    )}
                </div>
            )}

            {reformatBrief ? (
                <>
                    {viewMode === 'text' ? (
                        <textarea
                            className="brief-response-box"
                            value={reformatBrief}
                            onChange={handleReformatContentChange}
                        />
                    ) : (
                        <div
                            className="brief-response-box"
                            contentEditable
                            ref={contentEditableRef}
                            onInput={handleHtmlInput}
                        />
                    )}
                </>
            ) : (
                <div className="brief-empty">.</div>
            )}
            {isAdmin && briefResponse?.sourcePrompt && (
                <div className="brief-source-prompt">
                    <div className="brief-source-prompt-title">Source prompt:</div>
                    <pre className="brief-source-prompt-content">{briefResponse.sourcePrompt}</pre>
                </div>
            )}
            {loadingBrief && (
                <div className="spinner-overlay">
                    <LottieLoader size={600} />
                </div>
            )}
        </div>
    );
};

export default BriefComponent;
